import React, { createContext, useContext, useState, useEffect } from 'react';
import T from 'prop-types';
import _ from 'lodash';
import axios from 'axios';

import { makeAbsUrl } from '../utils/history';

const axiosData = axios.create({
  baseURL: makeAbsUrl('/assets/data')
});

// Context
export const BaseDataContext = createContext(null);
const oilOrder = ['ultra-light', 'light', 'medium', 'heavy', 'extra-heavy'];
const gasOrder = ['coal-based', 'dry', 'acid', 'wet', 'condensate'];

function customSort(a, b) {
  const wordsA = a.toLowerCase().split(' ');
  const wordsB = b.toLowerCase().split(' ');

  let predefinedKeywords;
  if (wordsA[1] == 'oil') {
    predefinedKeywords = oilOrder;
  } else {
    predefinedKeywords = gasOrder;
  }

  // Compare the position of keywords in the predefined sorted array

  const keywordA = wordsA[0];
  const keywordB = wordsB[0];

  const indexA = predefinedKeywords.indexOf(keywordA);
  const indexB = predefinedKeywords.indexOf(keywordB);

  if (indexA < indexB) {
    return -1;
  } else if (indexA > indexB) {
    return 1;
  }
  // If all keywords match, compare the phrase lengths
  return wordsA.length - wordsB.length;
}

// Context provider
export const BaseDataProvider = ({ children }) => {
  const [baseDataLoading, setBaseDataLoading] = useState(true);
  const [baseData, setBaseData] = useState({});

  useEffect(() => {
    async function dataLoader() {
      setBaseDataLoading(true);
      const [metadata, infoRaw] = await Promise.all([
        axiosData.get('metadata.json'),
        axiosData.get('oilinfo.json')
      ]);

      const info = infoRaw.data;

      const regions = _(infoRaw.data).map('Region').uniq().value();

      const opecCountries = _(infoRaw.data)
        .filter((e) => e.OPEC == 'Y')
        .map('Country')
        .map((e) => e.trim())
        .uniq()
        .value();

      const countries = _(infoRaw.data).map('Country').uniq().value();

      // Impute the state/province code from the trailing 2-letter token of
      // the asset name (e.g. "Placerita CA" → "CA"). Offshore assets without
      // a trailing code are excluded.
      const statesByCountry = _(infoRaw.data)
        .filter((e) => e.Country === 'United States' || e.Country === 'Canada')
        .reduce((acc, e) => {
          const match = (e['RMI Name'] || '').match(/\s([A-Z]{2})$/);
          if (!match) {
            return acc;
          }
          const code = match[1];
          if (!acc[e.Country]) {
            acc[e.Country] = new Set();
          }
          acc[e.Country].add(code);
          return acc;
        }, {});
      Object.keys(statesByCountry).forEach((c) => {
        statesByCountry[c] = [...statesByCountry[c]].sort();
      });

      const typesRaw = _(infoRaw.data).map('Resource Type').uniq().value();
      const oils = typesRaw
        .filter((e) => e.toLowerCase().includes('oil'))
        .sort(customSort);

      const gases = typesRaw
        .filter((e) => !e.toLowerCase().includes('oil'))
        .sort(customSort);
      let types = [];
      for (let i = 0; i < typesRaw.length; i++) {
        if (i % 2 == 0) {
          types.push(oils[Math.floor(i / 2)]);
        } else {
          types.push(gases[Math.floor(i / 2)]);
        }
      }

      // Format model parameters - Convert to array to simplify usage.
      const modelParameters = _.map(metadata.data, (param, id) => {
        // Convert the param values for visual representation.
        // The parameter values are stored in a string format like "1, .5, 2".
        // These values need to be converted and ordered before being displayed
        // to the user as selection options, but the value index (after
        // splitting by comma) needs to be stored, because this value is used to
        // create the name of the model to load.
        // There are also come specific cases that require special handling.

        // This code standardizes the values converting "1, .5, 2" to:
        // [
        //   {
        //     "value": 1,
        //     "origVal": 0.5,
        //     "label": "Low",
        //     "initial": false
        //   },
        //   {
        //     "value": 0,
        //     "origVal": 1,
        //     "label": "Default",
        //     "initial": true
        //   },
        //   {
        //     "value": 2,
        //     "origVal": 1.5,
        //     "label": "High",
        //     "initial": false
        //   }
        // ]
        const paramValues = _(param.values)
          .split(',')
          .map(Number)
          .thru((values) => {
            if (param.type === 'slider') {
              if (['venting', 'fugitives'].includes(id)) {
                const labels = ['Low', 'Default', 'High'];
                return (
                  _(values)
                    // map to ensure a common structure.
                    .map((v, i) => ({
                      value: i,
                      origVal: v,
                      label: v,
                      initial: i === 0
                    }))
                    // Sort the values
                    .sortBy('origVal')
                    // Replace the labels with the specific ones for this case.
                    .map((v, i) => ({ ...v, label: labels[i] }))
                    .value()
                );
              }

              return _(values)
                .map((v, i) => ({
                  value: i,
                  origVal: v,
                  label: `${v * 100}%`,
                  initial: i === 0
                }))
                .sortBy('origVal')
                .value();
            }

            return values.map((v, i) => ({
              value: i,
              origVal: v,
              label: v,
              initial: i === 0
            }));
          })
          .value();

        return {
          ...param,
          id,
          values: paramValues
        };
      });

      setBaseData({
        modelParameters,
        metadata: metadata.data,
        info,
        countries,
        statesByCountry,
        regions,
        types,
        opecCountries
      });

      setBaseDataLoading(false);
    }

    dataLoader();
  }, []);

  const contextValue = {
    baseDataLoading,
    baseData
  };

  return (
    <BaseDataContext.Provider value={contextValue}>
      {children}
    </BaseDataContext.Provider>
  );
};

BaseDataProvider.propTypes = {
  children: T.node
};

// Context consumers.

// Defaults for consumers that render outside the App-level `isLoading` gate
// (e.g. the ChartSettingsDrawers) and may read base data before it resolves.
const EMPTY_BASE_DATA = {
  modelParameters: [],
  metadata: {},
  info: [],
  countries: [],
  statesByCountry: {},
  regions: [],
  types: [],
  opecCountries: []
};

export const useBaseData = () => {
  const { baseDataLoading, baseData } = useContext(BaseDataContext);

  return {
    isLoading: baseDataLoading,
    ...EMPTY_BASE_DATA,
    ...baseData
  };
};
